import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Loader } from "@package/subframe/components/Loader";
import { Select } from "@package/subframe/components/Select";
import { FeatherPaperclip, FeatherShield } from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import {
  ComplianceTaskDefinitionResponseDto,
  ComplianceTaskDefinitionResponseDtoLatestInstanceStatus as DefStatus,
} from "@/generated/model";
import { dueRelativeLabel } from "@/lib/compliance-manager";
import { NURSERY_ROLE_LABELS } from "@/lib/roles";

import { sortCategoryIdsByCatalog } from "../compliance/compliance-constants";

// Status priority for sorting — overdue surfaces first, completed sinks last.
// Definitions with no current open instance (latestInstanceStatus null) go
// between active and completed: they're not failing, but the manager probably
// wants to see them before historical completions.
const STATUS_PRIORITY: Record<string, number> = {
  [DefStatus.OVERDUE]: 0,
  [DefStatus.AWAITING_EVIDENCE]: 1,
  [DefStatus.DUE_SOON]: 2,
  [DefStatus.NOT_YET_DUE]: 3,
  NULL: 4,
  [DefStatus.COMPLETED]: 5,
  [DefStatus.CANCELLED]: 6,
};

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getStatusBadge(status: DefStatus | undefined | null) {
  switch (status) {
    case DefStatus.OVERDUE:
      return <Badge variant="error">Overdue</Badge>;
    case DefStatus.DUE_SOON:
      return <Badge variant="warning">Due soon</Badge>;
    case DefStatus.AWAITING_EVIDENCE:
      return (
        <Badge variant="warning" icon={<FeatherPaperclip />}>
          Awaiting certificate
        </Badge>
      );
    case DefStatus.COMPLETED:
      return <Badge variant="success">Up to date</Badge>;
    case DefStatus.NOT_YET_DUE:
      return <Badge variant="neutral">Open</Badge>;
    case DefStatus.CANCELLED:
      return <Badge variant="neutral">Cancelled</Badge>;
    default:
      return <Badge variant="neutral">No instance</Badge>;
  }
}

interface ComplianceTasksTabProps {
  definitions: ComplianceTaskDefinitionResponseDto[];
  isLoading: boolean;
}

export function ComplianceTasksTab({
  definitions,
  isLoading,
}: ComplianceTasksTabProps) {
  const navigate = useNavigate();
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [showInactive, setShowInactive] = useState(false);

  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = categoriesResponse?.data.items ?? [];
  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));

  const visible = useMemo(() => {
    const filtered = definitions.filter((d) => {
      if (!showInactive && !d.isActive) return false;
      if (categoryFilter && !d.categoryIds.includes(categoryFilter))
        return false;
      return true;
    });

    return filtered.sort((a, b) => {
      const priorityA = STATUS_PRIORITY[a.latestInstanceStatus ?? "NULL"] ?? 99;
      const priorityB = STATUS_PRIORITY[b.latestInstanceStatus ?? "NULL"] ?? 99;
      if (priorityA !== priorityB) return priorityA - priorityB;
      // Within same status bucket, soonest due first; null next-due sinks.
      const dueA = a.nextDueDate
        ? new Date(a.nextDueDate).getTime()
        : Number.POSITIVE_INFINITY;
      const dueB = b.nextDueDate
        ? new Date(b.nextDueDate).getTime()
        : Number.POSITIVE_INFINITY;
      if (dueA !== dueB) return dueA - dueB;
      return a.name.localeCompare(b.name);
    });
  }, [definitions, categoryFilter, showInactive]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-8">
        <Loader size="medium" />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Filters */}
      <div className="flex w-full flex-wrap items-end gap-3">
        <Select
          label="Category"
          placeholder="All categories"
          value={categoryFilter || "all"}
          onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}
        >
          <Select.Item value="all">All categories</Select.Item>
          {categories.map((category) => (
            <Select.Item key={category.id} value={category.id}>
              {category.name}
            </Select.Item>
          ))}
        </Select>
        <button
          type="button"
          onClick={() => setShowInactive((v) => !v)}
          className="text-caption font-caption text-subtext-color hover:text-neutral-900 underline-offset-2 hover:underline pb-2"
        >
          {showInactive ? "Hide inactive tasks" : "Show inactive tasks"}
        </button>
      </div>

      {/* Empty state */}
      {visible.length === 0 ? (
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">
            {definitions.length === 0
              ? "No compliance tasks set up for this nursery yet"
              : `No compliance tasks match the current filters`}
          </span>
        </div>
      ) : (
        <div className="w-full overflow-x-auto rounded-sm bg-white">
          {/* Table header */}
          <div
            className="flex w-full items-center gap-4 pl-4 pr-4 py-2 border-b border-neutral-border"
            style={{ minWidth: 1096 }}
          >
            <span className="grow shrink-0 basis-0 text-caption-bold font-caption-bold text-neutral-500">
              Task
            </span>
            <span className="w-56 flex-none text-caption-bold font-caption-bold text-neutral-500">
              Categories
            </span>
            <span className="w-32 flex-none text-caption-bold font-caption-bold text-neutral-500">
              Assignee
            </span>
            <span className="w-36 flex-none text-caption-bold font-caption-bold text-neutral-500">
              Last completed
            </span>
            <span className="w-36 flex-none text-caption-bold font-caption-bold text-neutral-500">
              Next due
            </span>
            <span className="w-32 flex-none text-caption-bold font-caption-bold text-neutral-500 text-center">
              Status
            </span>
            <span className="w-16 flex-none"></span>
          </div>

          {/* Rows */}
          {visible.map((def, index) => {
            const overdue = def.latestInstanceStatus === DefStatus.OVERDUE;
            // Status-aware, so a completed definition can't read "Up to date"
            // and "overdue" at once — nextDueDate holds the completed instance's
            // own past due date once a series has no open successor (NAU-348).
            const relativeDue = def.nextDueDate
              ? dueRelativeLabel(
                  def.nextDueDate,
                  def.latestInstanceStatus ?? "",
                )
              : "";
            // Colour off the label, not the row status: an AWAITING_EVIDENCE
            // instance past its due date is genuinely late and should read red,
            // matching the manager hub.
            const relativeDueIsLate = relativeDue === "Overdue";

            return (
              <div className="w-full" key={def.id}>
                <div
                  className={`flex w-full items-center gap-4 rounded-sm pl-4 pr-4 py-3 ${
                    overdue ? "border-l-4 border-error-500 bg-error-50/40" : ""
                  } ${def.isActive ? "" : "opacity-60"}`}
                  style={{ minWidth: 1096 }}
                >
                  {/* Task name */}
                  <div className="flex grow shrink-0 basis-0 items-center gap-2 min-w-0">
                    <FeatherShield className="h-4 w-4 text-subtext-color flex-none" />
                    <div className="flex flex-col items-start min-w-0">
                      <span className="text-body-bold font-body-bold text-neutral-900 truncate w-full">
                        {def.name}
                      </span>
                      {!def.isActive && (
                        <span className="text-caption font-caption text-subtext-color">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Category */}
                  <div className="flex w-56 flex-none flex-wrap items-center gap-1">
                    {def.primaryCategoryId && (
                      <Badge variant="brand">
                        {categoryNameById.get(def.primaryCategoryId) ??
                          "Unknown"}
                      </Badge>
                    )}
                    {sortCategoryIdsByCatalog(
                      def.categoryIds.filter(
                        (id) => id !== def.primaryCategoryId,
                      ),
                      categories,
                    ).map((id) => (
                      <Badge key={id} variant="neutral">
                        {categoryNameById.get(id) ?? "Unknown"}
                      </Badge>
                    ))}
                  </div>

                  {/* Assignee */}
                  <div className="flex w-32 flex-none items-center">
                    {def.assigneeRole ? (
                      <Badge variant="neutral">
                        {NURSERY_ROLE_LABELS[def.assigneeRole] ??
                          def.assigneeRole}
                      </Badge>
                    ) : def.assigneeUserId ? (
                      <Badge variant="neutral">Assigned</Badge>
                    ) : (
                      <span className="text-caption font-caption text-subtext-color">
                        —
                      </span>
                    )}
                  </div>

                  {/* Last completed */}
                  <div className="flex w-36 flex-none items-center">
                    <span className="text-caption font-caption text-neutral-700">
                      {formatDate(def.lastCompletedAt)}
                    </span>
                  </div>

                  {/* Next due */}
                  <div className="flex w-36 flex-none flex-col items-start gap-0.5">
                    <span
                      className={`text-caption ${
                        overdue
                          ? "text-error-700 font-caption-bold"
                          : "text-neutral-700 font-caption"
                      }`}
                    >
                      {formatDate(def.nextDueDate)}
                    </span>
                    {relativeDue && (
                      <span
                        className={`text-caption font-caption ${
                          relativeDueIsLate
                            ? "text-error-600"
                            : "text-subtext-color"
                        }`}
                      >
                        {relativeDue}
                      </span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex w-32 flex-none items-center justify-center">
                    {getStatusBadge(def.latestInstanceStatus)}
                  </div>

                  {/* View */}
                  <div className="flex w-16 flex-none items-center justify-end">
                    <Button
                      variant="neutral-primary"
                      onClick={() =>
                        navigate({
                          to: "/compliance/definition/$definitionId",
                          params: { definitionId: def.id },
                        })
                      }
                    >
                      View
                    </Button>
                  </div>
                </div>

                {/* Row divider */}
                {index < visible.length - 1 && (
                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Count of *active* definitions whose latestInstanceStatus is OVERDUE.
 * Used by the dashboard tab label so managers see the count without opening
 * the tab. Excludes inactive definitions because deactivated tasks shouldn't
 * trip the "(N overdue)" alarm.
 */
export function countOverdueDefinitions(
  definitions: ComplianceTaskDefinitionResponseDto[] | undefined,
): number {
  if (!definitions) return 0;
  return definitions.filter(
    (d) => d.isActive && d.latestInstanceStatus === DefStatus.OVERDUE,
  ).length;
}
